import { useCallback, useEffect, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { FloatingAfricanKeyboard } from '@/components/keyboard';
import { useSessionDeepLink } from '@/hooks/use-session-deeplink';
import {
  TaskActionBar,
  TaskHeader,
  TaskSessionComplete,
  TaskShell,
  TaskStepDots,
  fillCopy,
  kitLang,
  taskSteps,
} from '@/features/contribution-kit';
import {
  CharacterSlot,
  LabelImageCard,
  LabelImagesError,
  LabelImagesLoading,
  LabelImagesSetup,
  extractCharacter,
  finalAccuracy,
  imageOutcome,
  isSessionStarted,
  labelImagesCopy,
  runningAccuracy,
  useLabelSession,
} from '@/features/label-images';

/** "Images": say which handwritten character each image shows, ten images per session. */
export default function LabelImages() {
  const { language } = useApp();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentLang = lang || language || 'fr';
  const copy = labelImagesCopy[kitLang(currentLang)];

  const [showKeyboard, setShowKeyboard] = useState(false);

  useEffect(() => {
    return () => setShowKeyboard(false);
  }, []);

  const session = useLabelSession();
  const sessionStarted = isSessionStarted(session.state);

  const handleKeyboardChange = useCallback(
    (val: string) => {
      session.setSelectedCharacter(extractCharacter(val));
    },
    [session],
  );

  const { startLinkedSession } = useSessionDeepLink({
    searchParams,
    setSearchParams,
    sessionValue: 'label-images',
    sessionStarted,
    startSession: session.startSession,
    requireLanguage: false,
  });

  const goHome = () => navigate(`/${currentLang}`);

  if (session.state === 'loading') return <LabelImagesLoading copy={copy} />;

  if (session.state === 'error') return <LabelImagesError copy={copy} message={session.error} onBack={goHome} />;

  if (!sessionStarted) {
    return <LabelImagesSetup copy={copy} imageCount={session.totalImages} onStart={() => void startLinkedSession()} onBack={goHome} />;
  }

  if (session.state === 'completed') {
    return (
      <TaskSessionComplete
        title={copy.doneTitle}
        description={copy.doneDescription}
        stats={[
          { label: copy.doneAccuracy, value: `${finalAccuracy(session.correctCount, session.totalImages)} %`, tone: 'success' },
          { label: copy.doneCorrect, value: session.correctCount },
          { label: copy.doneTotal, value: session.totalImages },
        ]}
        continueLabel={copy.doneContinue}
        hint={copy.doneHint}
        onContinue={() => void session.restartSession()}
        onHome={goHome}
      />
    );
  }

  const current = session.images[session.currentImage];
  const isLast = session.currentImage === session.totalImages - 1;
  const answered = session.correctAnswers.length;
  const steps = taskSteps(session.totalImages, session.currentImage, (index) => imageOutcome(session.correctAnswers, index));

  return (
    <>
      <TaskShell
        width="wide"
        header={
          <TaskHeader
            task="images"
            index={session.currentImage}
            total={session.totalImages}
            positionLabel={fillCopy(copy.imageXofY, { current: session.currentImage + 1, total: session.totalImages })}
            progress={session.sessionProgress}
            itemId={session.sessionId && current ? `${session.sessionId}:${current.index}` : null}
            onBack={goHome}
          />
        }
        actionBar={
          <TaskActionBar
            actions={[
              { key: 'previous', label: copy.previous, icon: 'arrowLeft', onClick: session.previousImage, disabled: session.currentImage === 0, shortcut: 'ArrowLeft' },
              { key: 'skip', label: copy.skip, icon: 'chevronRight', onClick: session.skipImage, shortcut: 'ArrowRight', testId: 'label-images-skip' },
              {
                key: 'validate',
                label: isLast ? copy.finish : copy.validate,
                icon: isLast ? 'checkCircle' : 'arrowRight',
                tone: 'primary',
                onClick: session.validateAnswer,
                disabled: !session.selectedCharacter,
                shortcut: 'Enter',
                testId: 'label-images-validate',
              },
            ]}
          />
        }
      >
        <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
          {current ? (
            <LabelImageCard
              copy={copy}
              imageUrl={current.imageUrl}
              imageIndex={session.currentImage}
              footer={
                <div className="space-y-2">
                  <TaskStepDots
                    steps={steps}
                    labels={{ current: copy.stepCurrent, done: copy.stepCorrect, rejected: copy.stepWrong, todo: copy.stepTodo }}
                  />
                  <p className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                    <span className="tabular-nums">{fillCopy(copy.answered, { answered, total: session.totalImages })}</span>
                    <span className="tabular-nums">{fillCopy(copy.accuracy, { accuracy: runningAccuracy(session.correctAnswers) })}</span>
                  </p>
                </div>
              }
            />
          ) : (
            <div />
          )}
          <CharacterSlot
            copy={copy}
            value={session.selectedCharacter}
            keyboardOpen={showKeyboard}
            onToggleKeyboard={() => setShowKeyboard((open) => !open)}
          />
        </div>
      </TaskShell>

      <AnimatePresence>
        {showKeyboard && (
          <FloatingAfricanKeyboard
            language={lang || 'français'}
            value={session.selectedCharacter}
            onChange={handleKeyboardChange}
            onClose={() => setShowKeyboard(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}
